import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { searchUsers } from "../Services/SocialService";
import {
  searchSong,
  searchVenues,
  type SongHit,
  type VenueHit,
} from "../Services/SearchService";
import { musicStatus } from "../utils/venueMusic";
import { venuePath } from "../utils/routes";
import { formatInitials } from "../utils/format";
import type { SocialUser } from "../data/types";
import "../Styles/HeaderSearch.css";

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

type Props = {
  /** Musiksök kräver ingen inloggning, personsök gör det. */
  canSearchPeople: boolean;
};

export default function HeaderSearch({ canSearchPeople }: Props) {
  const [q, setQ] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [people, setPeople] = useState<SocialUser[]>([]);
  const [venues, setVenues] = useState<VenueHit[]>([]);
  const [songs, setSongs] = useState<SongHit[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const trimmed = q.trim();
  const hasQuery = trimmed.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (!hasQuery) {
      setPeople([]);
      setVenues([]);
      setSongs([]);
      setIsLoading(false);
      return;
    }

    let alive = true;
    setIsLoading(true);

    const timer = window.setTimeout(async () => {
      const [peopleResult, venueResult, songResult] = await Promise.all([
        canSearchPeople
          ? searchUsers(trimmed).catch(() => [] as SocialUser[])
          : Promise.resolve([] as SocialUser[]),
        // Ställen är publika, som musiken.
        searchVenues(trimmed).catch(() => [] as VenueHit[]),
        searchSong(trimmed).catch(() => [] as SongHit[]),
      ]);

      if (!alive) return;

      setPeople(peopleResult);
      setVenues(venueResult.slice(0, 5));
      setSongs(songResult.slice(0, 5));
      setIsLoading(false);
    }, DEBOUNCE_MS);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [trimmed, hasQuery, canSearchPeople]);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const openSearch = () => {
    setIsOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const goToProfile = (user: SocialUser) => {
    setIsOpen(false);
    setQ("");
    navigate(`/profile/${user.id}`);
  };

  const goToVenue = (venue: VenueHit) => {
    setIsOpen(false);
    setQ("");
    navigate(venuePath(venue.id));
  };

  const showResults = isOpen && hasQuery;
  const hasResults =
    people.length > 0 || venues.length > 0 || songs.length > 0;

  return (
    <div className="header-search-wrapper" ref={wrapperRef}>
      <div
        className={[
          "header-search",
          isOpen || q ? "header-search--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          className="header-search-button"
          type="button"
          onClick={openSearch}
          aria-label="Open search"
        >
          <span aria-hidden="true">⌕</span>
        </button>

        <input
          ref={inputRef}
          className="header-search-input"
          placeholder={
            canSearchPeople
              ? "Search people, venues or music..."
              : "Search venues or music..."
          }
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setIsOpen(true)}
        />

        {q && (
          <button
            className="header-search-clear"
            type="button"
            onClick={() => {
              setQ("");
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {showResults && (
        <div className="header-search-results">
          {isLoading && !hasResults && (
            <p className="header-search-status">Searching…</p>
          )}

          {!isLoading && !hasResults && (
            <p className="header-search-status">No matches for “{trimmed}”</p>
          )}

          {people.length > 0 && (
            <section className="header-search-section">
              <p className="header-search-label">People</p>

              {people.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  className="header-search-row"
                  onClick={() => goToProfile(person)}
                >
                  <div className="header-search-avatar">
                    {person.avatarUrl ? (
                      <img src={person.avatarUrl} alt="" />
                    ) : (
                      <span>{formatInitials(person.displayName)}</span>
                    )}
                  </div>

                  <div className="header-search-row-text">
                    <strong>{person.displayName}</strong>
                    <small>
                      {person.isSelf
                        ? "This is you"
                        : person.followState === "following"
                        ? "Following"
                        : person.followState === "requested"
                        ? "Request sent"
                        : "Not following"}
                    </small>
                  </div>
                </button>
              ))}
            </section>
          )}

          {venues.length > 0 && (
            <section className="header-search-section">
              <p className="header-search-label">Venues</p>

              {venues.map((venue) => {
                // Spelar det något nu är det mer användbart än adressen.
                const music = musicStatus(venue.item);
                const track =
                  music.kind === "live" || music.kind === "recent"
                    ? music.track
                    : null;

                return (
                  <button
                    key={venue.id}
                    type="button"
                    className="header-search-row"
                    onClick={() => goToVenue(venue)}
                  >
                    <div className="header-search-avatar">
                      {venue.imageUrl ? (
                        <img src={venue.imageUrl} alt="" />
                      ) : (
                        <span>{formatInitials(venue.name)}</span>
                      )}
                    </div>

                    <div className="header-search-row-text">
                      <strong>{venue.name}</strong>
                      <small>
                        {track
                          ? `${music.kind === "live" ? "Playing: " : ""}${track.title} — ${track.artist}`
                          : venue.address ?? "Venue"}
                      </small>
                    </div>
                  </button>
                );
              })}
            </section>
          )}

          {songs.length > 0 && (
            <section className="header-search-section">
              {/* Katalogen är det Beatspot faktiskt har hört, inte hela världens musik. */}
              <p className="header-search-label">Heard on Beatspot</p>

              {songs.map((song) => (
                <div key={song.id} className="header-search-row is-static">
                  <div className="header-search-cover">
                    {song.albumCover ? (
                      <img src={song.albumCover} alt="" />
                    ) : (
                      <span>♪</span>
                    )}
                  </div>

                  <div className="header-search-row-text">
                    <strong>{song.title}</strong>
                    <small>{song.artist}</small>
                  </div>
                </div>
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
